import React from 'react';
import { BOOKING_URL } from '../../../constants/config';
import {
  Container,
  Typography,
  Grid,
  Box,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Alert,
  AlertTitle,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StarIcon from '@mui/icons-material/Star'; // For gold standard/results
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser'; // For durability/permanence
import SpaIcon from '@mui/icons-material/Spa'; // For comfort / why choose us
import BiotechIcon from '@mui/icons-material/Biotech'; // For advanced planning/technology
import MedicalServicesIcon from '@mui/icons-material/MedicalServices'; // For surgical aspect
import SyncIcon from '@mui/icons-material/Sync'; // For osseointegration (healing process)
import ConstructionIcon from '@mui/icons-material/Construction'; // For abutment/crown (building the tooth)
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'; // For "Is it right for you?"
import SupportIcon from '@mui/icons-material/Support'; // For bone support/preservation
import RestaurantIcon from '@mui/icons-material/Restaurant'; // For chewing function
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

const DentalImplantsPacificaDental = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const benefits = [
    'Provide a permanent and lifelike replacement for missing teeth.',
    'Look, feel, and function just like your natural teeth.',
    'Prevent jawbone loss and maintain your facial structure.',
    'Do not require alteration of adjacent healthy teeth (unlike bridges).',
    'Restore comfortable chewing ability and clear speech.',
    'Offer exceptional durability and can last a lifetime with proper care.',
    'High success rates, making them a reliable long-term solution.'
  ];

  const implantJourney = [
    {
      title: '1. Comprehensive Consultation & Advanced Planning',
      description: 'Your implant journey at Pacifica Dental begins with a thorough oral examination, including 3D CBCT scans for precise assessment of your jawbone. We\'ll discuss your goals, answer your questions, and create a personalized treatment plan.',
      icon: <BiotechIcon color="primary" sx={{ mr: 1, fontSize: '1.3rem' }} />
    },
    {
      title: '2. Gentle Implant Placement Surgery',
      description: 'The titanium implant post, acting as an artificial tooth root, is surgically and gently placed into your jawbone under local anesthesia to ensure your comfort. We prioritize a sterile and precise procedure.',
      icon: <MedicalServicesIcon color="primary" sx={{ mr: 1, fontSize: '1.3rem' }} />
    },
    {
      title: '3. Healing & Osseointegration',
      description: 'Over the next few months, a crucial process called osseointegration occurs, where the implant fuses securely with your jawbone. This creates a strong foundation for your new tooth. A temporary restoration may be used during this period.',
      icon: <SyncIcon color="primary" sx={{ mr: 1, fontSize: '1.3rem' }} />
    },
    {
      title: '4. Abutment Placement',
      description: 'Once healing is complete, a small connector post called an abutment is attached to the top of the implant. This piece will hold your new custom-made tooth.',
      icon: <ConstructionIcon color="primary" sx={{ mr: 1, fontSize: '1.3rem' }} />
    },
    {
      title: '5. Crafting & Attaching Your New Tooth',
      description: 'Impressions of your mouth are taken to create a custom, natural-looking crown (or bridge/denture). Once fabricated, this beautiful restoration is securely attached to the abutment, completing your new smile.',
      icon: <StarIcon color="primary" sx={{ mr: 1, fontSize: '1.3rem' }} />
    }
  ];

  const whyReplaceMissingTeeth = [
    'Prevent jawbone deterioration that occurs when teeth are missing.',
    'Stop adjacent teeth from shifting into the empty space, causing bite problems.',
    'Maintain the natural shape of your face and smile.',
    'Restore your ability to chew comfortably and speak clearly.',
    'Improve your overall oral health and self-confidence.'
  ];

  const isCandidateForImplants = [
    'You are missing one or more teeth.',
    'You have adequate jawbone density to support an implant (or are a candidate for bone grafting).',
    'You have good overall oral and general health.',
    'Your gums are healthy and free of periodontal disease.',
    'You are a non-smoker or willing to stop smoking during the healing process.',
    'You are committed to good oral hygiene and regular dental visits to Pacifica Dental.'
  ];

  const caringForImplants = [
    'Brush twice daily and floss at least once a day, paying special attention around the implant.',
    'Use interdental brushes or other recommended cleaning aids if advised by our team.',
    'Attend regular dental check-ups and professional cleanings at Pacifica Dental as scheduled.',
    'Avoid chewing extremely hard items like ice or hard candy on your implant crown.',
    'If you clench or grind your teeth, discuss a nightguard with your dentist.'
  ];
  
  const faqs = [
    {
      question: 'What are dental implants?',
      answer: 'Dental implants are small, biocompatible titanium posts that are surgically placed into the jawbone to serve as artificial tooth roots. They provide a strong foundation for fixed (permanent) or removable replacement teeth (crowns, bridges, or dentures).'
    },
    {
      question: 'How long does the dental implant process take?',
      answer: 'The entire process can take several months, typically from 3 to 9 months, depending on your individual healing time, bone health, and whether procedures like bone grafting are needed. Pacifica Dental will provide a personalized timeline.'
    },
    {
      question: 'Is dental implant surgery painful?',
      answer: 'The surgery is performed under local anesthesia, so you should not feel pain during the procedure. Post-operative discomfort is generally mild to moderate and manageable with over-the-counter pain relievers or prescribed medication. Most patients report less discomfort than anticipated.'
    },
    {
        question: 'How long do dental implants last?',
        answer: 'With proper care and regular dental check-ups at Pacifica Dental, dental implants can last a lifetime. The crown or restoration attached to the implant may need replacement due to normal wear over 10-15+ years.'
    },
    {
        question: 'What is the success rate of dental implants?',
        answer: 'Dental implants have a very high success rate, typically reported at 95% or higher, making them one of the most reliable tooth replacement options.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Dental Implants | Pacifica Dental | Permanent Tooth Replacement</title>
        <meta name="description" content="Restore your smile and function with permanent dental implants at Pacifica Dental. The gold standard for missing teeth. Call (408) 869-0998 for a consultation." />
        <meta name="keywords" content="dental implants Pacifica Dental, tooth replacement, implant dentistry Pacifica Dental, missing teeth solutions, permanent tooth replacement, (408) 869-0998, San Jose CA dental implants, Bay Area implant dentist" />
      </Helmet>
      
      <Box 
        sx={{ 
          py: { xs: 6, md: 8 },
          background: 'linear-gradient(135deg, #f5f7fa 0%, #e9eef3 100%)',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant={isMobile ? "h3" : "h2"}
              component="h1"
              textAlign="center"
              gutterBottom
              sx={{ 
                mb: 2,
                fontWeight: 700,
                color: 'primary.main'
              }}
            >
              Rediscover Your Smile with Dental Implants at Pacifica Dental
            </Typography>

            <Typography
              variant={isMobile ? "h6" : "h5"}
              component="h2"
              textAlign="center"
              gutterBottom
              sx={{ mb: 4, color: 'text.secondary', fontWeight: 500 }}
            >
              The Gold Standard for Permanent, Natural-Looking Tooth Replacement
            </Typography>

            <Typography
              variant="body1"
              paragraph
              sx={{ mb: 4, fontSize: '1.15rem', lineHeight: 1.8, color: 'text.primary' }}
            >
              Losing a tooth can significantly impact your confidence, chewing ability, and overall oral health. At Pacifica Dental, we offer state-of-the-art dental implants, the most advanced and reliable solution for replacing missing teeth. Implants provide a strong, stable, and aesthetically pleasing foundation for new teeth that look, feel, and function just like your own, allowing you to eat, speak, and smile with complete confidence again.
            </Typography>
            <Divider sx={{ my: 4 }} />

            {/* Benefits Section */}
            <Grid container spacing={isMobile ? 3 : 5} alignItems="stretch">
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={3}
                  sx={{
                    p: isMobile ? 2 : 3,
                    height: '100%',
                    backgroundColor: theme.palette.mode === 'light' ? 'primary.light' : 'primary.dark',
                    color: 'white',
                    borderRadius: 2
                  }}
                >
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 2}}>
                    The Unparalleled Benefits of Dental Implants
                  </Typography>
                  <List dense>
                    {benefits.map((benefit, index) => (
                      <ListItem key={index} sx={{ py: 0.5}}>
                        <ListItemIcon sx={{minWidth: 'auto', marginRight: 1.5}}>
                           {index % 3 === 0 ? <VerifiedUserIcon sx={{ color: 'white', fontSize: '1.4rem' }} /> : 
                            index % 3 === 1 ? <SupportIcon sx={{ color: 'white', fontSize: '1.4rem' }} /> :
                            <RestaurantIcon sx={{ color: 'white', fontSize: '1.4rem' }} />
                           }
                        </ListItemIcon>
                        <ListItemText primary={benefit} primaryTypographyProps={{fontSize: '1.05rem'}} />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>

              {/* Implant Journey Section */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={3}
                  sx={{
                    p: isMobile ? 2 : 3,
                    height: '100%',
                    borderRadius: 2
                  }}
                >
                  <Typography variant="h5" gutterBottom color="primary.main" sx={{ fontWeight: 600, mb: 2}}>
                    Your Dental Implant Journey at Pacifica Dental
                  </Typography>
                  {implantJourney.map((step, index) => (
                    <Box key={index} sx={{ mb: index === implantJourney.length - 1 ? 0 : 2.5 }}>
                       <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5 }}>
                         {step.icon}
                        <Typography variant="h6" color="primary.dark" fontWeight="bold" sx={{fontSize: '1.1rem'}}>
                          {step.title}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{fontSize: '0.95rem', lineHeight: 1.6, pl: '2.1rem'}}>
                        {step.description}
                      </Typography>
                    </Box>
                  ))}
                </Paper>
              </Grid>
            </Grid>

            <Divider sx={{ my: 5 }} /> 

            {/* Why Replace Missing Teeth? Section */}
            <Box sx={{ my: 5 }}>
              <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                The Importance of Replacing Missing Teeth
              </Typography>
              <Paper elevation={2} sx={{p:3, borderRadius: 2, background: theme.palette.mode === 'light' ? '#fff3e0' : '#4e342e' }}> {/* Light orange background */}
                <Typography variant="body1" paragraph sx={{ mb: 2, fontSize: '1.1rem', color: 'text.secondary' }}>
                  A missing tooth is more than just a gap in your smile; it can lead to several oral health issues if not addressed:
                </Typography>
                <Grid container spacing={1}>
                    {whyReplaceMissingTeeth.map((item, index) => (
                        <Grid item xs={12} sm={6} key={index}>
                            <List dense sx={{py:0}}>
                                <ListItem sx={{py:0.2, alignItems: 'flex-start'}}>
                                    <ListItemIcon sx={{minWidth: 'auto', marginRight: 1, color: 'primary.dark', mt:0.5}}>
                                        <CheckCircleOutlineIcon fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText primary={item} primaryTypographyProps={{fontSize: '1rem'}}/>
                                </ListItem>
                            </List>
                        </Grid>
                    ))}
                </Grid>
              </Paper>
            </Box>

            <Divider sx={{ my: 5 }} />
            
            {/* Are You a Candidate? Section */}
            <Box sx={{ my: 5 }}>
              <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                Are Dental Implants the Right Solution for You?
              </Typography>
              <Alert severity="info" icon={<HelpOutlineIcon />} sx={{borderRadius: 1.5, p:2, mb:3 }} elevation={2}>
                <AlertTitle sx={{fontWeight: 'bold'}}>Personalized Assessment is Key</AlertTitle>
                A comprehensive evaluation at Pacifica Dental is necessary to determine if you are an ideal candidate for dental implants. We'll review your dental and medical history and discuss your specific needs.
              </Alert>
              <Grid container spacing={2}>
                {isCandidateForImplants.map((item, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Paper elevation={1} sx={{p:1.5, display: 'flex', alignItems: 'center', height: '100%', borderRadius: 1.5}}>
                      <CheckCircleOutlineIcon color="success" sx={{mr: 1.5, fontSize: '1.8rem'}} />
                      <Typography variant="body1" sx={{fontSize: '1rem'}}>{item}</Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Divider sx={{ my: 5 }} />

            {/* Caring For Your Dental Implants Section */}
            <Box sx={{ my: 5 }}>
                <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                    Ensuring the Longevity of Your Dental Implants
                </Typography>
                 <Paper elevation={2} sx={{p:3, borderRadius: 2, background: theme.palette.mode === 'light' ? '#e8f5e9' : '#1b5e20' }}> {/* Light green background */}
                    <Typography variant="body1" paragraph sx={{ mb: 1, fontSize: '1.1rem', color: 'text.primary' }}>
                        Dental implants are designed to last many years, even a lifetime, with proper care. Treat them just like your natural teeth:
                    </Typography>
                    <List>
                        {caringForImplants.map((tip, index) => (
                            <ListItem key={index}>
                                <ListItemIcon sx={{minWidth: 'auto', marginRight: 1.5, color: 'primary.dark'}}>
                                    <VerifiedUserIcon />
                                </ListItemIcon>
                                <ListItemText primary={tip} primaryTypographyProps={{fontSize: '1rem'}}/>
                            </ListItem>
                        ))}
                    </List>
                </Paper>
            </Box>
            
            <Divider sx={{ my: 5 }} />

            {/* FAQ Section */}
            <Box sx={{ my: 5 }}>
              <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                Dental Implant FAQs at Pacifica Dental
              </Typography>
              {faqs.map((faq, index) => (
                <Accordion key={index} elevation={2} sx={{ mb: 1.5, borderRadius: 1, '&:before': { display: 'none' } }}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon color="primary"/>}
                    aria-controls={`faq-panel${index}-content`}
                    id={`faq-panel${index}-header`}
                    sx={{ '& .MuiAccordionSummary-content': { fontWeight: 'medium', fontSize: '1.1rem' } }}
                  >
                    {faq.question}
                  </AccordionSummary>
                  <AccordionDetails sx={{backgroundColor: theme.palette.mode === 'light' ? '#fafafa' : '#333' }}>
                    <Typography color="text.secondary" sx={{lineHeight: 1.7, fontSize: '1rem'}}>
                      {faq.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>

            <Divider sx={{ my: 5 }} />

            {/* Why Choose Pacifica Dental Section */}
            <Box sx={{ mt: 6, p:3, background: theme.palette.mode === 'light' ? 'rgba(0, 123, 255, 0.05)' : 'rgba(100,100,100,0.1)', borderRadius:2 }}>
              <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb:3 }}>
                Why Choose Pacifica Dental for Your Dental Implants?
              </Typography>
              <Grid container spacing={3}>
                  {[
                      { title: "Experienced Implant Dentists", text: "Our skilled dental team at Pacifica Dental has extensive training and experience in all aspects of implant dentistry, from planning to placement and restoration.", icon: <VerifiedUserIcon color="primary" /> },
                      { title: "Advanced Diagnostic Technology", text: "We utilize cutting-edge technology, including 3D CBCT imaging, for precise diagnosis, meticulous planning, and predictable surgical outcomes.", icon: <BiotechIcon color="primary" /> },
                      { title: "High-Quality Implant Systems", text: "Pacifica Dental uses only reputable, clinically proven implant systems and materials to ensure the highest standards of quality and long-term success.", icon: <StarIcon color="primary" /> },
                      { title: "Comprehensive & Compassionate Care", text: "We provide personalized care throughout your entire implant journey, ensuring your comfort and addressing all your concerns with empathy and expertise.", icon: <SpaIcon color="primary" /> }
                  ].map(item => (
                      <Grid item xs={12} sm={6} key={item.title}>
                          <Box sx={{display: 'flex', alignItems: 'flex-start'}}>
                              <ListItemIcon sx={{minWidth: 'auto', marginRight: 1.5, marginTop: 0.5}}>
                                  {item.icon}
                              </ListItemIcon>
                              <Box>
                                  <Typography variant="h6" component="h4" sx={{fontWeight: 'bold', fontSize: '1.1rem'}}>{item.title}</Typography>
                                  <Typography variant="body2" color="text.secondary">{item.text}</Typography>
                              </Box>
                          </Box>
                      </Grid>
                  ))}
              </Grid>
            </Box>


            {/* Call to Action */}
            <Box sx={{ mt: 6, py: 4, textAlign: 'center', background: theme.palette.primary.main, color: 'white', borderRadius: 2 }}>
                <Typography variant={isMobile ? "h5" : "h4"} gutterBottom sx={{fontWeight: 600}}>
                    Ready to Reclaim Your Smile with Dental Implants?
                </Typography>
                <Typography variant="body1" sx={{mb:3, opacity: 0.9}}>
                    Discover the life-changing benefits of dental implants. Contact Pacifica Dental today to schedule your comprehensive consultation.
                </Typography>
              <Button
                onClick={() => window.location.href = BOOKING_URL}
                variant="contained"
                color="secondary"
                size="large"
                sx={{
                  px: 5, 
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1.1rem',
                  '&:hover': {
                    backgroundColor: 'secondary.dark',
                  }
                }}
              >
                Schedule Your Implant Consultation
              </Button>
              <Typography variant="body2" sx={{mt: 2, opacity: 0.8}}>
                Or call Pacifica Dental at <Link to="tel:4088690998" style={{color: 'white', fontWeight: 'bold'}}>(408) 869-0998</Link> to learn more.
              </Typography>
            </Box>

          </motion.div>
        </Container>
      </Box>
    </>
  );
};

export default DentalImplantsPacificaDental;